import { useEffect, useState } from "react";
import { Radio } from "lucide-react";
import api from "@/lib/api";

/**
 * Visual style: neon-cyan bordered channel tiles with glow on hover/active,
 * matching the user's mockup. Click → swaps the active channel for the
 * parent <PlayerPage> via onChange. Empty channels are still clickable
 * so listeners can see what's coming.
 */
export default function ChannelSelector({ value, onChange }) {
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);

  // Default 5 genres always shown — actual mix counts merged in from /api/channels
  const DEFAULTS = ["House", "EDM", "Cumbia", "Salsa", "Bachata"];

  useEffect(() => {
    let cancelled = false;
    api.get("/channels").then((r) => {
      if (cancelled) return;
      setChannels(r.data?.channels || []);
    }).finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, []);

  // Merge: ensure all 5 default genres show (with count=0 if empty)
  const byId = new Map(channels.map((c) => [c.id, c]));
  const merged = DEFAULTS.map((name) => {
    const id = name.toLowerCase();
    const found = byId.get(id);
    return found || { id, name, count: 0, total_duration: 0 };
  });

  const isActive = (id) => (value || "all") === id;

  return (
    <div className="w-full mb-7" data-testid="channel-selector">
      <div className="flex items-center justify-center gap-1.5 mb-3 text-[10px] font-mono uppercase tracking-[0.25em] text-[var(--neon-cyan)]">
        <Radio size={12} /> Select Your Channel
      </div>

      {/* Grid: 3 across on top row, 2 on second (matches mockup) + "All" pill at bottom */}
      <div className="grid grid-cols-3 gap-2 max-w-[320px] mx-auto">
        {merged.slice(0, 3).map((c) => (
          <ChannelTile
            key={c.id}
            channel={c}
            active={isActive(c.id)}
            onClick={() => onChange?.(c.id)}
          />
        ))}
      </div>
      <div className="grid grid-cols-2 gap-2 max-w-[210px] mx-auto mt-2">
        {merged.slice(3, 5).map((c) => (
          <ChannelTile
            key={c.id}
            channel={c}
            active={isActive(c.id)}
            onClick={() => onChange?.(c.id)}
          />
        ))}
      </div>

      <div className="flex justify-center mt-3">
        <button
          type="button"
          onClick={() => onChange?.("all")}
          data-testid="channel-tile-all"
          className={`px-4 py-1.5 text-[10px] font-mono uppercase tracking-[0.25em] rounded-full border transition-all ${
            isActive("all")
              ? "border-[var(--neon-cyan)] bg-[rgba(0,242,254,0.1)] text-[var(--neon-cyan)] shadow-[0_0_15px_rgba(0,242,254,0.3)]"
              : "border-[var(--border-soft)] text-[var(--text-muted)] hover:border-[var(--border-cyan)] hover:text-[var(--neon-cyan)]"
          }`}
        >
          ▶ All Genres
        </button>
      </div>
    </div>
  );
}

function ChannelTile({ channel, active, onClick }) {
  const isEmpty = channel.count === 0;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`channel-tile-${channel.id}`}
      className={`
        relative h-16 rounded-md font-display font-bold text-base tracking-[0.15em]
        flex items-center justify-center
        border transition-all duration-200
        ${active
          ? "border-[var(--neon-cyan)] bg-[rgba(0,242,254,0.08)] text-[var(--neon-cyan)] shadow-[0_0_20px_rgba(0,242,254,0.35),inset_0_0_15px_rgba(0,242,254,0.08)]"
          : isEmpty
          ? "border-[var(--border-soft)] text-[var(--text-muted)] opacity-60 hover:opacity-100 hover:border-[var(--border-cyan)] hover:text-[var(--neon-cyan)]"
          : "border-[var(--border-cyan)] text-[var(--neon-cyan)] hover:bg-[rgba(0,242,254,0.05)] hover:shadow-[0_0_18px_rgba(0,242,254,0.25)]"
        }
      `}
    >
      {channel.name.toUpperCase()}
      {/* tiny count badge if channel has tracks */}
      {channel.count > 0 && (
        <span className="absolute top-1 right-1.5 text-[8px] font-mono opacity-60">
          {channel.count}
        </span>
      )}
      {/* "empty" hint */}
      {isEmpty && (
        <span className="absolute bottom-0.5 right-1 text-[7px] font-mono uppercase tracking-[0.2em] opacity-50">
          soon
        </span>
      )}
    </button>
  );
}
